"use client";
import { useLanguage } from "@web/lib/contexts/language-context";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

interface SecurityItemsCarousel {
  items?: any[];
}
const MAX_DOTS = 10;

export function SecurityItemsCarousel({ items }: SecurityItemsCarousel) {
  const { t } = useLanguage();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [startX, setStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<Record<number, boolean>>({});
  const [direction, setDirection] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    items?.forEach((item, index) => {
      const img = new Image();
      img.src = item.image || "/placeholder.svg";
      img.onload = () => {
        setImagesLoaded((prev) => ({ ...prev, [index]: true }));
      };
    });
  }, [items]);

  useEffect(() => {
    if (!items?.length) return;

    if (currentIndex >= items?.length && items?.length > 0) {
      setCurrentIndex(0);
    } else if (items?.length === 0 && currentIndex !== 0) {
      setCurrentIndex(0);
    }
  }, [items, currentIndex]);

  const handlePrev = useCallback(() => {
    if (!items?.length) return;
    setDirection(-1);
    setCurrentIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : items.length - 1,
    );
  }, [items?.length]);

  const handleNext = useCallback(() => {
    if (!items?.length) return;
    setDirection(1);
    setCurrentIndex((prevIndex) =>
      prevIndex < items.length - 1 ? prevIndex + 1 : 0,
    );
  }, [items?.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        handlePrev();
      } else if (e.key === "ArrowRight") {
        handleNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNext, handlePrev]);

  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsDragging(true);
    setStartX("touches" in e ? e.touches[0].clientX : e.clientX);
  };

  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDragging) return;
    const currentX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const diff = currentX - startX;
    setDragOffset(diff);
  };

  const handleTouchEnd = () => {
    if (!isDragging || !items?.length) return;
    setIsDragging(false);

    if (Math.abs(dragOffset) > 50) {
      if (dragOffset > 0) {
        handlePrev();
      } else {
        handleNext();
      }
    }

    setDragOffset(0);
  };

  if (!items?.length) {
    return (
      <div className="w-full text-center py-8">
        <p className="text-gray-500">
          {t.qrCodePage.securityItems.config.noSecurityItems}
        </p>
      </div>
    );
  }

  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (dir: number) => ({
      zIndex: 0,
      x: dir < 0 ? 300 : -300,
      opacity: 0,
    }),
  };
  const renderDots = () => {
    const total = items.length;

    if (total <= MAX_DOTS) {
      return items.map((item, index) => (
        <button
          type="button"
          key={item.title}
          className={`h-2.5 rounded-full transition-all duration-200 ${index === currentIndex
            ? "bg-primary w-6"
            : "bg-gray-300 w-2.5 hover:bg-gray-400"
            }`}
          onClick={() => {
            setDirection(index > currentIndex ? 1 : -1);
            setCurrentIndex(index);
          }}
          aria-label={`${t.qrCodePage.securityItems.config.goToItem} ${index + 1}`}
        />
      ));
    }

    const half = Math.floor(MAX_DOTS / 2);
    let windowStart = currentIndex - half;
    let windowEnd = currentIndex + (MAX_DOTS - half - 1);

    if (windowStart < 0) {
      windowStart = 0;
      windowEnd = MAX_DOTS - 1;
    }
    if (windowEnd >= total) {
      windowEnd = total - 1;
      windowStart = total - MAX_DOTS;
    }

    const windowIndices = Array.from(
      { length: MAX_DOTS },
      (_, i) => windowStart + i,
    );

    return windowIndices.map((index) => {
      const isEdge = index === windowStart || index === windowEnd;
      const isCurrent = index === currentIndex;

      return (
        <button
          type="button"
          key={index}
          className={`rounded-full transition-all duration-200 ${isCurrent
            ? "bg-primary w-6 h-2.5"
            : isEdge
              ? "bg-gray-200 w-1.5 h-1.5"
              : "bg-gray-300 w-2.5 h-2.5 hover:bg-gray-400"
            }`}
          onClick={() => {
            setDirection(index > currentIndex ? 1 : -1);
            setCurrentIndex(index);
          }}
          aria-label={`${t.qrCodePage.securityItems.config.goToItem} ${index + 1}`}
        />
      );
    });
  };

  return (
    <div className="w-full select-none">

      <div className="hidden">
        {items.map((item, index) => (
          <img
            key={`preload-${index}`}
            src={item.image || "/placeholder.svg"}
            alt=""
            aria-hidden="true"
          />
        ))}
      </div>

      <div className="relative overflow-hidden rounded-lg">
        {items.length > 1 && (
          <>
            <button
              type="button"
              onClick={handlePrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-lg hover:bg-white transition-all duration-200 hover:scale-110"
              aria-label="Item anterior"
            >
              <ChevronLeft className="h-5 w-5 text-gray-700" />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-lg hover:bg-white transition-all duration-200 hover:scale-110"
              aria-label="Próximo item"
            >
              <ChevronRight className="h-5 w-5 text-gray-700" />
            </button>
          </>
        )}

        {/** biome-ignore lint/a11y/noStaticElementInteractions: <explanation> */}
        <div
          ref={carouselRef}
          className="relative h-[300px] w-full touch-pan-y cursor-grab active:cursor-grabbing"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleTouchStart}
          onMouseMove={handleTouchMove}
          onMouseUp={handleTouchEnd}
          onMouseLeave={handleTouchEnd}
        >
          <AnimatePresence initial={false} mode="sync" custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="absolute inset-0 flex flex-col items-center"
              transition={{ type: "keyframes" }}
            >
              <div className="relative flex flex-col w-full items-center justify-center overflow-hidden rounded-lg h-[200px]">
                {!imagesLoaded[currentIndex] && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                )}
                <img
                  src={items[currentIndex].image || "/placeholder.svg"}
                  alt={items[currentIndex].title}
                  className={`h-full object-contain transition-opacity duration-300 ${imagesLoaded[currentIndex] ? "opacity-100" : "opacity-0"}`}
                />
              </div>
              <div className="mt-4 text-center px-4">
                <h3 className="text-sm font-bold">
                  {currentIndex + 1} - {items[currentIndex].title}
                </h3>
                <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                  {items[currentIndex].description}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      <div className="mt-4 flex flex-col items-center gap-2">
        <div className="text-xs font-medium text-gray-500">
          {currentIndex + 1} de {items.length}
        </div>
        <div className="flex items-center justify-center gap-2">
          {renderDots()}
        </div>
      </div>

      <div className="mt-3 text-center text-xs text-gray-400">
        <p>{t.qrCodePage.securityItems.config.useArrows}</p>
      </div>
    </div>
  );
}